/**
 * AgentStatusCompact — compacte beslis-status voor de 9 agents.
 *
 * Feb 2026 owner-cockpit redesign: vervangt de illusie dat agents autonoom
 * draaien. Elke agent toont één van vijf labels en — waar toepasbaar — de
 * volgende beslissing die owner kan nemen.
 *
 * Labels:
 *   - "Uit"                 = agent staat aan/staat uit; geen activiteit.
 *   - "Alleen lezen"        = agent observeert / scant; doet niets autonoom.
 *   - "Voorstel klaar"      = agent heeft voorstel in Approval Inbox.
 *   - "Wacht op goedkeuring"= owner moet Ja/Nee/Aanpassen kiezen.
 *   - "Live"                = agent voert geverifieerd uit, met veiligheidsguard.
 */
import { Shield, Mail, Megaphone, Plug, Handshake, Sparkles, BarChart3, Activity, CreditCard } from 'lucide-react';

const STATUS_STYLES = {
  off:        { dot: 'bg-white/35',    text: 'text-white/60',   border: 'border-white/15',     label: 'Uit' },
  readonly:   { dot: 'bg-sky-300',     text: 'text-sky-200',    border: 'border-sky-400/40',   label: 'Alleen lezen' },
  proposal:   { dot: 'bg-amber-300',   text: 'text-amber-200',  border: 'border-amber-400/40', label: 'Voorstel klaar' },
  approval:   { dot: 'bg-amber-300 animate-pulse', text: 'text-amber-200', border: 'border-amber-400/50', label: 'Wacht op goedkeuring' },
  live:       { dot: 'bg-emerald-300', text: 'text-emerald-200',border: 'border-emerald-400/40', label: 'Live' },
};

const AGENTS = [
  // Werkend / deels werkend
  { id: 'stripe',          name: 'Betaling / Stripe',   Icon: CreditCard,  status: 'live',     last: 'iDEAL + kaart + SEPA-incasso actief op productie', next: 'Eerste echte €9,95 betalingstest', safety: 'Stripe-gehard' },
  { id: 'claim_sweep',     name: 'Claim-sweep',         Icon: Shield,      status: 'live',     last: 'Landing: 0 forbidden hits',                       next: 'Continu monitoring',               safety: 'Read-only' },
  { id: 'pilot_demo',      name: 'Pilot / demo-app',    Icon: Activity,    status: 'live',     last: 'Mijn Powersure /app dark UI + onboarding-sheet',  next: 'Eerste echte klant in /app',       safety: 'Per-pilot JWT, geen PII' },
  { id: 'action_center',   name: 'Owner Action Center', Icon: Sparkles,    status: 'approval', last: 'Items wachten in Approval Inbox',                next: 'Goedkeuren / aanpassen / negeren',  safety: 'Approve = interne status' },
  // Niet live / approval-first
  { id: 'mail_agent',      name: 'Mail Agent',          Icon: Mail,        status: 'readonly', last: 'EMAIL_DRY_RUN=true — geen mail verstuurd',       next: 'Owner GO om live te zetten',       safety: 'Dry-run' },
  { id: 'ads_agent',       name: 'Ads Agent',           Icon: Megaphone,   status: 'approval', last: 'Voorstellen wachten op goedkeuring',             next: 'Owner approve voor publicatie',     safety: 'Budget-guard €800' },
  { id: 'provider_agent',  name: 'Provider Agent',      Icon: Handshake,   status: 'readonly', last: 'Verrijking via openbare bronnen',                next: 'Owner approve voor outreach',       safety: 'Geen mail zonder approval' },
  { id: 'device_agent',    name: 'Device Agent',        Icon: Plug,        status: 'readonly', last: 'Inventarisatie/onboarding zonder sturing',       next: 'Approve per device voor sturing',   safety: 'Sturing alleen met OK' },
  { id: 'affiliate_agent', name: 'Affiliate Agent',     Icon: BarChart3,   status: 'proposal', last: 'Link-suggesties klaar voor review',              next: 'Owner approve per product',         safety: 'Read-only voorstel' },
];

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.off;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${s.border} ${s.text}`}
      data-testid={`agent-status-${status}`}
    >
      <span className={`w-1 h-1 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

export default function AgentStatusCompact() {
  return (
    <div data-testid="agent-status-compact" className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-white/55">
          Agentstatus — approval-first
        </h3>
        <span className="text-[10px] text-white/45">{AGENTS.length} agents</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
        {AGENTS.map((a) => (
          <div
            key={a.id}
            data-testid={`agent-card-${a.id}`}
            className="rounded-xl bg-white/5 border border-white/10 p-3 hover:border-emerald-400/30 transition-colors"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 shrink-0">
                <a.Icon className="w-3.5 h-3.5" />
              </span>
              <span className="flex-1 text-sm font-semibold text-white leading-tight" style={{ fontFamily: 'Outfit' }}>{a.name}</span>
              <StatusBadge status={a.status} />
            </div>
            <p className="text-[11px] text-white/65 leading-snug">
              <span className="text-white/45">Laatste:</span> {a.last}
            </p>
            <p className="text-[11px] text-white/65 leading-snug">
              <span className="text-white/45">Volgende:</span> {a.next}
            </p>
            <p className="text-[10px] text-white/45 leading-snug mt-1">
              <span className="text-white/40">Veiligheid:</span> {a.safety}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
